import React, { useEffect, useState } from 'react';
import { SkeletonText, TooltipIcon } from 'carbon-components-react';
import { Time20, Checkmark20, HelpFilled16 } from '@carbon/icons-react';

export const ReferralList: React.FC<Props | any> = (props: Props) => {
  const {
    referrals,
    responseReceivedText,
    handleResponseSelection,
    selectedReferral,
    handleMultipleResponseSelection,
    selectedResponse,
    selectionSource,
    isLoading,
    timestamp,
    enquiryDetail,
    responsesExpired,
    showResponsesExpiredModal
  } = props;

  const [isVisibleAnimation, setIsVisibleAnimation] = useState<any>({});

  useEffect(() => {
    if (referrals) {
      const viewResponseTimestamp: any = timestamp?.toISOString() || undefined;
      const filterComplete = referrals.filter((referral: any) => {
        return referral?.status === 'COMPLETED';
      });
      const sortedDataArray: any[] = [...filterComplete]?.sort(
        (a: any, b: any) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );
      const latestData = sortedDataArray[0];
      const initialAnimations: any = {};
      referrals.forEach((referral: any, index: number) => {
        if (
          referral?.isNewResponses &&
          referral?.updatedAt === latestData?.updatedAt &&
          (referral?.updatedAt >= viewResponseTimestamp ||
            !viewResponseTimestamp)
        ) {
          initialAnimations[index] = true;
        } else {
          initialAnimations[index] = false;
        }
      });
      setIsVisibleAnimation(initialAnimations);
    }
    // eslint-disable-next-line
  }, [referrals, enquiryDetail]);

  useEffect(() => {
    // Reset animation for any index that is currently true after 500 miliseconds
    const timeout = setTimeout(() => {
      setIsVisibleAnimation((prevState: any) => {
        const updatedAnimations = { ...prevState };
        Object.keys(updatedAnimations).forEach((index) => {
          if (updatedAnimations[index] === true) {
            updatedAnimations[index] = false;
          }
        });
        return updatedAnimations;
      });
    }, 500);
    return () => clearTimeout(timeout);
  }, [isVisibleAnimation]);

  const SingleResponse = (referral: any, index: number) => (
    <div
      className={`get-plans-actions-responses-listing-row ${
        referral.status === 'COMPLETED' && !responsesExpired
          ? `completed ${
              referral?.isNewResponses && isVisibleAnimation[index] && 'visible'
            }`
          : responsesExpired
          ? 'expired'
          : 'waiting'
      } ${
        selectionSource === 'responses' &&
        selectedReferral === index &&
        selectedResponse === -1
          ? 'selected'
          : ''
      }`}
      key={index}
      onClick={
        responsesExpired ? undefined : () => handleResponseSelection(index)
      }
    >
      <div
        className={`get-plans-actions-responses-listing-row-name ${
          selectionSource === 'responses' &&
          selectedReferral === index &&
          selectedResponse === -1 &&
          !(referral?.isNewResponses && isVisibleAnimation[index])
            ? 'indicator-selected'
            : ''
        }`}
      >
        {referral?.isNewResponses && isVisibleAnimation[index]
          ? ''
          : referral.name}
      </div>
      <div className="get-plans-actions-responses-listing-row-status">
        {referral.status === 'COMPLETED' ? (
          referral?.isNewResponses && isVisibleAnimation[index] ? (
            ''
          ) : (
            <Checkmark20 />
          )
        ) : (
          <Time20 />
        )}
      </div>
    </div>
  );

  const MultipleResponse = (referral: any, referralIndex: number) => (
    <div key={referralIndex}>
      <div
        className={`get-plans-actions-responses-listing-row ${
          referral.status === 'COMPLETED' &&
          `completed ${
            referral?.isNewResponses &&
            isVisibleAnimation[referralIndex] &&
            'visible'
          }`
        } ${responsesExpired ? 'expired' : ''}`}
        key={referralIndex}
      >
        <div className="get-plans-actions-responses-multiple-listing-row-name">
          {referral?.isNewResponses && isVisibleAnimation[referralIndex]
            ? ''
            : referral.name}
        </div>
        <div className="get-plans-actions-responses-listing-row-status">
          {referral.status === 'COMPLETED' ? (
            referral?.isNewResponses && isVisibleAnimation[referralIndex] ? (
              ''
            ) : (
              <Checkmark20 />
            )
          ) : (
            <Time20 />
          )}
        </div>
      </div>
      {!isVisibleAnimation[referralIndex] &&
        referral?.responses
          .sort((a: any, b: any) => a.responseId - b.responseId)
          .map((response: any, responseIndex: any) => (
            <div
              key={responseIndex}
              className="get-plans-actions-responses-multiple-listing-subRow"
            >
              <div
                className={`get-plans-actions-responses-multiple-listing-subRow-indicator ${
                  selectionSource === 'responses' &&
                  selectedResponse === responseIndex &&
                  selectedReferral === referralIndex
                    ? 'indicator-selected'
                    : ''
                }`}
              ></div>
              <div
                className={`get-plans-actions-responses-multiple-listing-row-response ${
                  responsesExpired ? 'expired' : 'completed'
                } 
              ${
                selectionSource === 'responses' &&
                selectedResponse === responseIndex &&
                selectedReferral === referralIndex
                  ? 'selected'
                  : ''
              }`}
                key={responseIndex}
                onClick={
                  responsesExpired
                    ? undefined
                    : () =>
                        handleMultipleResponseSelection(
                          referralIndex,
                          responseIndex
                        )
                }
              >
                Response {responseIndex + 1}
              </div>
              {/* )} */}
            </div>
          ))}
    </div>
  );

  return (
    <div
      className={`get-plans-actions-responses ${
        responsesExpired && showResponsesExpiredModal
          ? 'get-plans-actions-responses-expired'
          : ''
      }`}
    >
      {(!responsesExpired || !showResponsesExpiredModal) && (
        <>
          <div className="get-plans-actions-responses-header">
            <div>
              RESPONSES{' '}
              <span>
                (
                {isLoading
                  ? ''
                  : responsesExpired
                  ? 'unavailable'
                  : responseReceivedText}
                )
              </span>
            </div>
            <div>
              <TooltipIcon
                tooltipText={
                  'List of members responding to your enquiry, including responses still pending. Select a company from the list to view their responses, plans and contacts.'
                }
                className="tooltip enquiry-details-tooltip"
              >
                <HelpFilled16 className="tooltip-icon" />
              </TooltipIcon>
            </div>
          </div>
          <div className="get-plans-actions-responses-listing">
            {!isLoading &&
              referrals?.map((referral: any, index: any) =>
                referral.responses === undefined ||
                referral.responses?.length < 2
                  ? SingleResponse(referral, index)
                  : MultipleResponse(referral, index)
              )}
            {isLoading && (
              <div className="get-plans-actions-responses-listing-skeleton">
                {[1, 2].map((item: any) => (
                  <div
                    key={item}
                    className="get-plans-actions-responses-listing-skeleton-row"
                  >
                    <SkeletonText />
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

type ownProps = {
  referrals: any;
  responseReceivedText: string;
  handleResponseSelection: any;
  handleMultipleResponseSelection: any;
  selectedReferral: Number;
  selectedResponse: Number;
  selectionSource?: 'interactive-map' | 'responses';
  isLoading?: boolean;
  timestamp?: Date;
  enquiryDetail: any;
  responsesExpired: boolean;
  showResponsesExpiredModal: boolean;
};

type Props = ownProps;

ReferralList.displayName = 'ReferralList';

export default React.memo(ReferralList);
